import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./FAQs.css";

function FAQs() {
  document.title = "FAQs :: D-Right Technologies";

  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const [faqs, setFaqs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("General");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedItems, setExpandedItems] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [categoryLayout] = useState("tabs"); // Finalized to tabs layout
  const [faqLayout] = useState("minimal"); // Finalized to minimal layout
  const [searchLayout] = useState("badge-left"); // Finalized to badge-left layout
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetch("/faqs.json")
      .then((response) => response.json())
      .then((data) => {
        setFaqs(data.faqs || []);
        if (data.faqs && data.faqs.length > 0) {
          setSelectedCategory("General");
        }
      })
      .catch((error) => console.error("Error fetching FAQs:", error));
  }, []);

  useEffect(() => {
    if (faqs.length === 0) return;
    const fromUrl = categoryParam && faqs.some(cat => cat.category === categoryParam);
    const next = fromUrl ? categoryParam : "General";
    setSelectedCategory(next);
    if (fromUrl) {
      setSearchQuery("");
      setIsSearchMode(false);
    }
  }, [categoryParam, faqs]);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const results = [];
      
      faqs.forEach((category) => {
        category.items.forEach((item) => {
          const questionMatch = item.question.toLowerCase().includes(query);
          const answerMatch = item.answer.toLowerCase().includes(query);
          
          if (questionMatch || answerMatch) {
            results.push({
              ...item,
              category: category.category,
            });
          }
        });
      });
      
      setSearchResults(results);
      setIsSearchMode(true);
    } else {
      setSearchResults([]);
      setIsSearchMode(false);
    }
  }, [searchQuery, faqs]);

  const toggleAccordion = (categoryIndex, itemIndex) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleKeyDown = (e, categoryIndex, itemIndex) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleAccordion(categoryIndex, itemIndex);
    }
  };

  const handleSearchKeyDown = (e, resultIndex) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const key = `search-${resultIndex}`;
      setExpandedItems((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    }
  };

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
    setSearchQuery("");
    setIsSearchMode(false);
  };

  const getCurrentCategory = () => {
    return faqs.find((cat) => cat.category === selectedCategory);
  };

  // Render FAQ item in minimal layout
  const renderFAQItem = (item, itemIndex, categoryIndex, isSearchResult = false, resultCategory = null) => {
    const key = isSearchResult ? `search-${itemIndex}` : `${categoryIndex}-${itemIndex}`;
    const isExpanded = expandedItems[key] || false;
    const itemId = isSearchResult ? `search-result-${itemIndex}` : `faq-${categoryIndex}-${itemIndex}`;
    const headerId = `header-${itemId}`;
    const contentId = `content-${itemId}`;

    const toggleHandler = () => {
      if (isSearchResult) {
        setExpandedItems((prev) => ({
          ...prev,
          [key]: !prev[key],
        }));
      } else {
        toggleAccordion(categoryIndex, itemIndex);
      }
    };

    const keyDownHandler = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggleHandler();
      }
    };

    return (
      <div key={itemIndex} className="faq-item faq-item-minimal faq-minimal-item">
        <div
          className={`faq-item-header ${isExpanded ? "expanded" : ""}`}
          id={headerId}
          role="tab"
          aria-expanded={isExpanded}
          aria-controls={contentId}
          tabIndex={0}
          onClick={toggleHandler}
          onKeyDown={keyDownHandler}
        >
          {isSearchResult && resultCategory && (
            <div className="faq-category-wrapper-left">
              <span className="faq-category-badge faq-category-badge-left">{resultCategory}</span>
            </div>
          )}
          <h6 className="faq-question">
            {item.question}
            <span className={`faq-icon ${isExpanded ? "expanded" : ""}`} aria-hidden="true">
              <i className={`ti-angle-${isExpanded ? "up" : "down"}`}></i>
            </span>
          </h6>
        </div>
        <div
          id={contentId}
          className={`faq-item-content ${isExpanded ? "show" : ""}`}
          role="tabpanel"
          aria-labelledby={headerId}
        >
          <div className="faq-answer">
            <p>{item.answer}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Header />
      <div className="main no-banner-top-padding">
        {/* Page Header */}
        <section
          className="page-header-section ptb-100 bg-image"
          image-overlay="8"
        >
          <div
            className="background-image-wraper"
            style={{
              background: `url('/assets/img/slider-bg-1.jpg')`,
              opacity: "1",
            }}
          ></div>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-9 col-lg-7">
                <div className="page-header-content text-white pt-4">
                  <h1 className="text-white mb-0">Frequently Asked Questions</h1>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="faq-section ptb-100">
          <div className="container">
            {/* Search Bar - Centered at top */}
            <div className="faq-search-section mb-5">
              <div className="faq-mini-search faq-search-layout-centered">
                <div className="faq-search-centered-wrapper">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search FAQs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      ref={searchInputRef}
                      aria-label="Search frequently asked questions"
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">
                        <i className="ti-search" aria-hidden="true"></i>
                      </span>
                    </div>
                  </div>
                </div>
                {searchQuery.trim() && (
                  <small className="faq-search-count">
                    {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} found
                  </small>
                )}
              </div>
            </div>

            <div className="row">
              {/* Category Sidebar */}
              <div className="col-lg-3 col-md-4 mb-4 mb-md-0">
                <div className="faq-sidebar faq-category-layout-tabs">
                  <ul className="faq-category-list" role="tablist">
                    {faqs.map((category, index) => (
                      <li key={index} role="presentation">
                        <button
                          type="button"
                          className={`faq-category-item ${
                            selectedCategory === category.category && !isSearchMode
                              ? "active"
                              : ""
                          }`}
                          onClick={() => handleCategoryClick(category.category)}
                          role="tab"
                          aria-selected={selectedCategory === category.category && !isSearchMode}
                          aria-controls={`faq-category-${index}`}
                        >
                          {category.category}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="col-lg-9 col-md-8">
                {/* FAQ Content */}
                <div className="faq-content-wrapper faq-layout-minimal">
                  {/* FAQ Content */}
                  {isSearchMode ? (
                    // Search Results
                    searchResults.length > 0 ? (
                      <div className="faq-search-results faq-layout-minimal">
                        {searchResults.map((result, resultIndex) => 
                          renderFAQItem(result, resultIndex, 0, true, result.category)
                        )}
                      </div>
                    ) : (
                      <div className="faq-no-results text-center py-5">
                        <i className="ti-search faq-no-results-icon" aria-hidden="true"></i>
                        <p className="mt-3">No results found for "{searchQuery}"</p>
                        <p className="text-muted">Try different keywords or check your spelling</p>
                      </div>
                    )
                  ) : (
                    // Category FAQs
                    (() => {
                      const currentCategory = getCurrentCategory();
                      if (!currentCategory) return null;

                      const categoryIndex = faqs.findIndex(
                        (cat) => cat.category === selectedCategory
                      );

                      return (
                        <div className="faq-accordion-mode faq-layout-minimal">
                          <div
                            className="accordion faq-accordion faq-items-container faq-minimal-container"
                            id={`accordion-${categoryIndex}`}
                            role="tablist"
                          >
                            {currentCategory.items.map((item, itemIndex) => 
                              renderFAQItem(item, itemIndex, categoryIndex, false, null)
                            )}
                          </div>
                        </div>
                      );
                    })()
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}

export default FAQs;
